import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate, Outlet } from "react-router-dom";
import { getModulePath, LEAGUE_MODULE_NAMES } from "@leagueplatform/core";
import { APP_MANAGED_ROUTES } from "common/app-managed-routes";
import { BenefitsShell } from "components/benefits-shell/benefits-shell.component";
// Not lazy — this route only fires side effects on mount and immediately
// redirects, so a Suspense fallback would show the loader for the whole
// chunk-fetch window and the URL flash the fallback masks.
import { LiveChatEntryRoute } from "live-chat/live-chat-entry-route";
import { LiveChatPreChatRoute } from "live-chat/live-chat-pre-chat-route";
import { LandingRoute } from "pages/pre-login-landing/landing-route";
import { AuthSessionContainer } from "components/auth-session-container/auth-session-container";
import { JourneyRouteGuard } from "components/journey-route-guard/journey-route-guard.component";
import { FindCareRouteGuard } from "components/find-care-route-guard/find-care-route-guard.component";
import { CareCollectionsMemberRouteRedirect } from "components/care-collections-member-route-redirect/care-collections-member-route-redirect.component";
import { LegacyAboutMeRouteRedirect } from "components/legacy-about-me-route-redirect/legacy-about-me-route-redirect.component";

const AuthViewLogin = lazy(() =>
  import("pages/auth/auth.pages").then((m) => ({ default: m.AuthViewLogin }))
);

const AuthViewLogout = lazy(() =>
  import("pages/auth/auth.pages").then((m) => ({ default: m.AuthViewLogout }))
);

const MarketingMicroSite = lazy(() =>
  import("pages/marketing-microsite/marketing-microsite.page").then((m) => ({
    default: m.MarketingMicroSite,
  }))
);

const HomePage = lazy(() =>
  import("pages/home/home.page").then((m) => ({ default: m.HomePage }))
);

const HealthJourneyRoutes = lazy(() =>
  import("@leagueplatform/health-journey").then((m) => ({
    default: m.HealthJourneyRoutes,
  }))
);

const MessagingRoutes = lazy(() =>
  import("@leagueplatform/messaging").then((m) => ({
    default: m.MessagingRoutes,
  }))
);

const AboutMeRoutes = lazy(() =>
  import("@leagueplatform/about-me").then((m) => ({
    default: m.AboutMeRoutes,
  }))
);

const AchievementsRoutes = lazy(() =>
  import("@leagueplatform/rewards-achievements").then((m) => ({
    default: m.AchievementsRoutes,
  }))
);

const CareCollectionsRoutes = lazy(() =>
  import("@leagueplatform/care-collections").then((m) => ({
    default: m.CareCollectionsRoutes,
  }))
);

// Lazy on purpose: the module pulls in the map renderers, and its Masonry
// registry registers on import, which only matters once a Care Finder route
// is entered.
const CareFinderRoutes = lazy(() =>
  import("@leagueplatform/care-finder").then((m) => ({
    default: m.CareFinderRoutes,
  }))
);

const CarePage = lazy(() =>
  import("pages/care/care.page").then((m) => ({ default: m.CarePage }))
);

const PriorAuthorizationsPage = lazy(() =>
  import("pages/prior-authorizations/prior-authorizations.page").then((m) => ({
    default: m.PriorAuthorizationsPage,
  }))
);

// Emblem-owned details page — wraps the details Masonry root in the
// tenant's PageHero + PageContentWrapper chrome so it matches the
// landing page. Replaces the SDK's `PreAuthorizationDetailsPage`,
// which paints edge-to-edge with no hero.
const PriorAuthorizationDetailsPage = lazy(() =>
  import("pages/prior-authorizations/prior-authorization-details.page").then(
    (m) => ({
      default: m.PriorAuthorizationDetailsPage,
    })
  )
);

const ClaimsRoutes = lazy(() =>
  import("@leagueplatform/claims").then((m) => ({
    default: m.ClaimsRoutes,
  }))
);

const ProfilePage = lazy(() =>
  import("pages/profile/profile.page").then((m) => ({
    default: m.ProfilePage,
  }))
);

const MyProfilePage = lazy(() =>
  import("pages/my-profile/my-profile.page").then((m) => ({
    default: m.MyProfilePage,
  }))
);

const MyProfileFormPage = lazy(() =>
  import("pages/my-profile/my-profile-form.page").then((m) => ({
    default: m.MyProfileFormPage,
  }))
);

const DocumentsPage = lazy(() =>
  import("pages/documents/documents.page").then((m) => ({
    default: m.DocumentsPage,
  }))
);

const CarePlansPage = lazy(() =>
  import("pages/care-plans/care-plans.page").then((m) => ({
    default: m.CarePlansPage,
  }))
);

const FormsPage = lazy(() =>
  import("pages/forms/forms.page").then((m) => ({
    default: m.FormsPage,
  }))
);

const CareManagementFormPage = lazy(() =>
  import("pages/care-management-form/care-management-form.page").then((m) => ({
    default: m.CareManagementFormPage,
  }))
);

const MedicationCoverageFormPage = lazy(() =>
  import("pages/medication-coverage-form/medication-coverage-form.page").then(
    (m) => ({
      default: m.MedicationCoverageFormPage,
    })
  )
);

const CobInterstitialPage = lazy(() =>
  import("pages/cob-interstitial/cob-interstitial.page").then((m) => ({
    default: m.CobInterstitialPage,
  }))
);

const CobFormPage = lazy(() =>
  import("pages/cob-form/cob-form.page").then((m) => ({
    default: m.CobFormPage,
  }))
);

const MedicationCoverageRequestsPage = lazy(() =>
  import(
    "pages/medication-coverage-requests/medication-coverage-requests.page"
  ).then((m) => ({
    default: m.MedicationCoverageRequestsPage,
  }))
);

const PreAuthorizationRequestsPage = lazy(() =>
  import(
    "pages/pre-authorization-requests/pre-authorization-requests.page"
  ).then((m) => ({
    default: m.PreAuthorizationRequestsPage,
  }))
);

const PreAuthorizationFormPage = lazy(() =>
  import(
    "pages/pre-authorization-request-form/pre-authorization-request-form.page"
  ).then((m) => ({
    default: m.PreAuthorizationFormPage,
  }))
);

const PreAuthorizationCheckToolPage = lazy(() =>
  import(
    "pages/pre-authorization-check-tool/pre-authorization-check-tool.page"
  ).then((m) => ({
    default: m.PreAuthorizationCheckToolPage,
  }))
);

const PreAuthorizationCheckToolResultPage = lazy(() =>
  import(
    "pages/pre-authorization-check-tool/pre-authorization-check-tool-result.page"
  ).then((m) => ({
    default: m.PreAuthorizationCheckToolResultPage,
  }))
);

const StudentCertificationFormPage = lazy(() =>
  import(
    "pages/student-certification-form/student-certification-form.page"
  ).then((m) => ({
    default: m.StudentCertificationFormPage,
  }))
);

const SubmissionConfirmationPage = lazy(() =>
  import("pages/submission-confirmation/submission-confirmation.page").then(
    (m) => ({
      default: m.SubmissionConfirmationPage,
    })
  )
);

const SupportPage = lazy(() =>
  import("pages/support/support.page").then((m) => ({
    default: m.SupportPage,
  }))
);

const MemberSelectionPage = lazy(() =>
  import("pages/member-selection/member-selection.page").then((m) => ({
    default: m.MemberSelectionPage,
  }))
);

const BillingAndPaymentsPage = lazy(() =>
  import("pages/billing-and-payments/billing-and-payments.page").then((m) => ({
    default: m.BillingAndPaymentsPage,
  }))
);

const CommunicationPreferencesPage = lazy(() =>
  import("pages/communication-preferences/communication-preferences.page").then(
    (m) => ({
      default: m.CommunicationPreferencesPage,
    })
  )
);

const CommunicationPreferencesNotificationsPage = lazy(() =>
  import(
    "pages/communication-preferences/communication-preferences-notifications.page"
  ).then((m) => ({
    default: m.CommunicationPreferencesNotificationsPage,
  }))
);

const NotificationsPage = lazy(() =>
  import("pages/notifications/notifications.page").then((m) => ({
    default: m.NotificationsPage,
  }))
);

const DocumentPreferencesSuccessPage = lazy(() =>
  import(
    "pages/communication-preferences/document-preferences-success.page"
  ).then((m) => ({
    default: m.DocumentPreferencesSuccessPage,
  }))
);

const NotificationsSuccessPage = lazy(() =>
  import("pages/communication-preferences/notifications-success.page").then(
    (m) => ({
      default: m.NotificationsSuccessPage,
    })
  )
);

const AboutPage = lazy(() =>
  import("pages/about/about.page").then((m) => ({ default: m.AboutPage }))
);

const PolicySwitcherPage = lazy(() =>
  import("pages/policy-switcher/policy-switcher.page").then((m) => ({
    default: m.PolicySwitcherPage,
  }))
);

const ContactPage = lazy(() =>
  import("pages/contact/contact.page").then((m) => ({
    default: m.ContactPage,
  }))
);

const SsoVendorPage = lazy(() =>
  import("pages/sso/sso-vendor.page").then((m) => ({
    default: m.SsoVendorPage,
  }))
);

const SsoExchangePage = lazy(() =>
  import("pages/sso/sso-exchange.page").then((m) => ({
    default: m.SsoExchangePage,
  }))
);

const ErrorPage = lazy(() => import("pages/error-404/error-404.page"));

// Shared by both `/sso/vendors/:tenant` mounts so the Find Care flag is
// honored no matter which one matched.
const ssoVendorElement = (
  <FindCareRouteGuard>
    <SsoVendorPage />
  </FindCareRouteGuard>
);

export const appRoutes: RouteObject[] = [
  {
    path: APP_MANAGED_ROUTES.LANDING_PAGE,
    element: <LandingRoute />,
  },
  {
    path: APP_MANAGED_ROUTES.SIGN_IN,
    element: <AuthViewLogin />,
  },
  {
    path: APP_MANAGED_ROUTES.SIGN_OUT,
    element: <AuthViewLogout />,
  },
  {
    element: (
      <AuthSessionContainer>
        <Outlet />
      </AuthSessionContainer>
    ),
    children: [
      {
        path: APP_MANAGED_ROUTES.HOME,
        element: <HomePage />,
      },
      // Both SSO routes must be ordered above the `/benefits/*` wildcard so
      // React Router picks the more specific match (the benefits Masonry
      // subtree emits SSO URLs as relative paths under `/benefits/...`).
      { path: "/sso/vendors/:tenant", element: ssoVendorElement },
      {
        path: `${APP_MANAGED_ROUTES.BENEFITS}/sso/vendors/:tenant`,
        element: ssoVendorElement,
      },
      { path: "/sso/exchange", element: <SsoExchangePage /> },
      {
        path: `${APP_MANAGED_ROUTES.BENEFITS}/*`,
        element: <BenefitsShell />,
      },
      { path: APP_MANAGED_ROUTES.CARE, element: <CarePage /> },
      { path: APP_MANAGED_ROUTES.LIVE_CHAT, element: <LiveChatEntryRoute /> },
      {
        path: APP_MANAGED_ROUTES.LIVE_CHAT_PRE_CHAT,
        element: <LiveChatPreChatRoute />,
      },
      {
        path: APP_MANAGED_ROUTES.PRIOR_AUTHORIZATIONS,
        element: <PriorAuthorizationsPage />,
      },
      {
        path: APP_MANAGED_ROUTES.PRIOR_AUTHORIZATIONS_DETAILS,
        element: <PriorAuthorizationDetailsPage />,
      },
      {
        // Same page — the SDK's canonical `/pre-authorizations-details`
        // path, mounted so any deeplink emitted with that URL (SDK
        // helpers, docs, older BE trees) resolves the same view.
        path: APP_MANAGED_ROUTES.PRIOR_AUTHORIZATIONS_DETAILS_SDK_ALIAS,
        element: <PriorAuthorizationDetailsPage />,
      },
      { path: APP_MANAGED_ROUTES.CARE_PLANS, element: <CarePlansPage /> },
      { path: APP_MANAGED_ROUTES.FORMS, element: <FormsPage /> },
      {
        path: APP_MANAGED_ROUTES.CARE_MANAGEMENT_FORM,
        element: <CareManagementFormPage />,
      },
      {
        path: APP_MANAGED_ROUTES.MEDICATION_COVERAGE_FORM,
        element: <MedicationCoverageFormPage />,
      },
      {
        path: APP_MANAGED_ROUTES.COB_INTERSTITIAL,
        element: <CobInterstitialPage />,
      },
      {
        path: APP_MANAGED_ROUTES.COB_FORM,
        element: <CobFormPage />,
      },
      {
        path: APP_MANAGED_ROUTES.MEDICATION_COVERAGE_INTERSTITIAL,
        element: <MedicationCoverageRequestsPage />,
      },
      {
        path: APP_MANAGED_ROUTES.PRE_AUTHORIZATION_INTERSTITIAL,
        element: <PreAuthorizationRequestsPage />,
      },
      {
        path: APP_MANAGED_ROUTES.PRE_AUTHORIZATION_FORM,
        element: <PreAuthorizationFormPage />,
      },
      {
        path: APP_MANAGED_ROUTES.PRE_AUTHORIZATION_CHECK_TOOL,
        element: <PreAuthorizationCheckToolPage />,
      },
      {
        path: APP_MANAGED_ROUTES.PRE_AUTHORIZATION_CHECK_TOOL_RESULT,
        element: <PreAuthorizationCheckToolResultPage />,
      },
      {
        path: APP_MANAGED_ROUTES.STUDENT_CERTIFICATION_FORM,
        element: <StudentCertificationFormPage />,
      },
      {
        path: APP_MANAGED_ROUTES.SUBMISSION_CONFIRMATION,
        element: <SubmissionConfirmationPage />,
      },
      { path: APP_MANAGED_ROUTES.SUPPORT, element: <SupportPage /> },
      {
        path: APP_MANAGED_ROUTES.MEMBER_SELECTION,
        element: <MemberSelectionPage />,
      },
      {
        path: APP_MANAGED_ROUTES.BILLING_AND_PAYMENTS,
        element: <BillingAndPaymentsPage />,
      },
      {
        path: APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES,
        element: <CommunicationPreferencesPage />,
      },
      {
        path: APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES_NOTIFICATIONS,
        element: <CommunicationPreferencesNotificationsPage />,
      },
      {
        path: APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES_DOC_PREFS_SUCCESS,
        element: <DocumentPreferencesSuccessPage />,
      },
      {
        path: APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES_NOTIFICATIONS_SUCCESS,
        element: <NotificationsSuccessPage />,
      },
      {
        path: APP_MANAGED_ROUTES.NOTIFICATIONS,
        element: <NotificationsPage />,
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.claims)}/*`,
        element: <ClaimsRoutes />,
      },
      { path: APP_MANAGED_ROUTES.PROFILE, element: <ProfilePage /> },
      { path: APP_MANAGED_ROUTES.MY_PROFILE, element: <MyProfilePage /> },
      {
        path: APP_MANAGED_ROUTES.MY_PROFILE_PERSONAL_INFORMATION,
        element: <MyProfileFormPage screen="personalInformation" />,
      },
      {
        path: APP_MANAGED_ROUTES.MY_PROFILE_ADDRESS,
        element: <MyProfileFormPage screen="address" />,
      },
      {
        path: APP_MANAGED_ROUTES.MY_PROFILE_PHONE_AND_EMAIL,
        element: <MyProfileFormPage screen="phoneAndEmail" />,
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.careDocuments)}/*`,
        element: <DocumentsPage />,
      },
      { path: APP_MANAGED_ROUTES.ABOUT, element: <AboutPage /> },
      { path: APP_MANAGED_ROUTES.CONTACT, element: <ContactPage /> },
      {
        path: APP_MANAGED_ROUTES.SWITCH_ACCOUNT,
        element: <PolicySwitcherPage />,
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.messaging)}/*`,
        element: <MessagingRoutes />,
      },
      // Uses APP_MANAGED_ROUTES rather than getModulePath: this array is
      // built at import time, before `overwriteRoutePaths(routesConfig)`
      // runs in app.tsx, so getModulePath would still return `/about-me`.
      {
        path: `${APP_MANAGED_ROUTES.MY_HEALTH_DASHBOARD}/*`,
        element: <AboutMeRoutes />,
      },
      // The capability owns all of its own sub-routes (`/results`,
      // `/results/provider-details`, `/results/facility-details`,
      // `/comparison`). Mounted unconditionally — only the Find Care entry
      // point is flag-gated, same as Android.
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.careFinder)}/*`,
        element: <CareFinderRoutes />,
      },
      // The five journey-module base paths all render HealthJourneyRoutes
      // and are gated by the journey-nav flag — deeplinks to /health-journey
      // are intercepted to /home when contentConfig.journey.nav-enabled is
      // false.
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.healthJourney)}/*`,
        element: (
          <JourneyRouteGuard>
            <HealthJourneyRoutes />
          </JourneyRouteGuard>
        ),
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.healthProgress)}/*`,
        element: (
          <JourneyRouteGuard>
            <HealthJourneyRoutes />
          </JourneyRouteGuard>
        ),
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.healthPrograms)}/*`,
        element: (
          <JourneyRouteGuard>
            <HealthJourneyRoutes />
          </JourneyRouteGuard>
        ),
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.healthJourneyHistory)}/*`,
        element: (
          <JourneyRouteGuard>
            <HealthJourneyRoutes />
          </JourneyRouteGuard>
        ),
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.careLibrary)}/*`,
        element: (
          <JourneyRouteGuard>
            <HealthJourneyRoutes />
          </JourneyRouteGuard>
        ),
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.careCollections)}/*`,
        element: (
          <JourneyRouteGuard>
            <CareCollectionsRoutes />
          </JourneyRouteGuard>
        ),
      },
      {
        path: `${getModulePath(LEAGUE_MODULE_NAMES.rewardsAchievements)}/*`,
        element: (
          <JourneyRouteGuard>
            <AchievementsRoutes />
          </JourneyRouteGuard>
        ),
      },
      // Achievements empty-state CTA (`empty.cta.url` in the payload) is
      // hardcoded on shared BE to `/member/health-programs`. Emblem mounts
      // the health-programs module at `/health-programs`, so translate the
      // prefixed variant here until BE ships a tenant-specific override.
      {
        path: "/member/health-programs/*",
        element: <Navigate to="/health-programs" replace />,
      },
      {
        path: "/member/care-collections/*",
        element: <CareCollectionsMemberRouteRedirect />,
      },
      // Bookmarks and external links (emails, marketing pages) to the old
      // About me URL bypass the SDK's processUrl remapping.
      {
        path: "/about-me/*",
        element: <LegacyAboutMeRouteRedirect />,
      },
      {
        path: "*",
        element: <ErrorPage />,
      },
    ],
  },
  {
    path: "/marketing",
    children: [{ path: "*", element: <MarketingMicroSite /> }],
  },
];
